/**
 * Investigations port — the index of collaborative investigation case files.
 *
 * An investigation is a watch scope (what the team is following) plus a case
 * file (pinned evidence, shared notes, live discussion, an approval runbook).
 *
 * **Backed by `investigation-mgmt-api`.** Cases used to be `SavedView` rows at
 * `tableId: "investigations"`, with everything crammed into `state.extra`. That
 * made them visible to teammates — which the earlier `localStorage` index never
 * did — but it cost three things a case file needs and a view does not:
 *
 * 1. **No point read.** Saved views expose no by-id lookup, so `get(id)` listed
 *    every case in the tenant and searched client-side.
 * 2. **No room to grow.** Evidence, annotations, comments and workflow steps are
 *    all unbounded, and a saved view is one 400 KB DynamoDB item.
 * 3. **No verb of its own.** `shared-graphs:edit` authorized both "rename a saved
 *    graph" and "publish this case's annotation to the whole tenant".
 *
 * Each of those is now a route: `GET /investigations/:id`, four child collections
 * with their own tables, and an `investigations:*` scope family.
 *
 * The wire is **snake_case** (the backend's canonical field names, generated from
 * the Pydantic models); this module is the one place that translates, so nothing
 * in `features/investigations` has to know. Watch shapes are still validated on
 * read — the server canonicalises on write, but a row written by an older shape
 * must degrade to "not shown" rather than take down the list route it appears in.
 */
import { unwrapData, type ApiClient } from "@authe/fe-commons/api";
import type { ViewScope } from "@authe/saved-views";
import { parseActs, type CaseAct } from "@/features/investigations/caseActs";
import { isTxTenancyScopeId, type TxTenancyScopeId } from "@/features/transactions/scopes";
import type { TimeRangePreset } from "@authe/fe-commons/metadata-analytics";
import {
  DEFAULT_INVESTIGATION_STATE,
  DEFAULT_RETENTION,
  FEED_WINDOW_PRESETS,
  WINDOW_ALL,
  type FeedWindow,
  type InvestigationSaga,
  type InvestigationState,
  type InvestigationStatus,
  type Retention,
  type Watch,
} from "@/features/investigations/model";

/** Canonical FE path; `RESOURCE_REWRITES` maps it onto the service base path. */
const BASE = "/investigations";

/**
 * The reverse index's own prefix.
 *
 * Not under `BASE`: the question is "which cases pinned this", which has no case
 * to be under. `RESOURCE_REWRITES` maps it ahead of `/investigations` for exactly
 * that reason.
 */
const REFERENCES = "/evidence/references";

/** Exhibits per request. The route caps its own at 100; this is the ask. */
const EVIDENCE_PAGE_SIZE = 100;

/**
 * How many pages of one case's exhibits to walk.
 *
 * 500 exhibits is far past the point where a case is a case rather than a
 * dataset, and the bound is what keeps opening a runaway one from costing an
 * unbounded number of requests. The walk reports when it stopped here, so the
 * shortfall is visible rather than read as "that's all of them".
 */
export const MAX_EVIDENCE_PAGES = 5;

/**
 * Personal (`user`) vs Shared with the tenant (`tenant`).
 *
 * The backend ladder is wider (user → team → tenant → org → customer → system),
 * but these are the two tiers a person actually chooses between in the UI; the
 * rest are administrative. Typed as `ViewScope` so the existing scope pickers,
 * written when cases were saved views, keep working unchanged.
 */
export type InvestigationScope = ViewScope;

export interface Investigation extends InvestigationState {
  id: string;
  title: string;
  scope: InvestigationScope;
  /** Auth0 sub of the creator, when the backend recorded one. */
  createdBy: string | null;
  /**
   * How many exhibits the case holds, maintained by the server.
   *
   * A COUNT, not the ids: the ids live in the evidence collection and are read
   * through {@link InvestigationsSource.listEvidence}. This replaces a
   * `pinned: string[]` that the backend stopped sending when evidence moved out
   * of the case row — it parsed to `[]` on every case, so every surface reading
   * it (the graph's pinned-only filter, the retention notice, the list chip)
   * had been quietly answering "none" for every case in the tenant.
   */
  evidenceCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface CreateInvestigationInput {
  title: string;
  scope: InvestigationScope;
  watches?: Watch[];
  retention?: Partial<Retention>;
  saga?: InvestigationSaga;
}

/** What a case hands to the rest of the product, derived once on the server. */
export interface CaseHandoff {
  investigationId: string;
  /** CAIP-10 accounts the case follows directly. */
  addresses: string[];
  protocolIds: string[];
  chainIds: string[];
  /**
   * Watches whose subject no concrete scope can express — an entity, tag, label
   * or group can cover addresses nobody enumerated. Reported rather than
   * expanded: resolving one would build a scope the reader never chose.
   */
  unrepresented: { kind: string; catalogId: string; name: string | null }[];
  feedScope: TxTenancyScopeId;
  pipelineId: string | null;
}

/** What a case can pin. Mirrors `commons…investigation_evidence.EvidenceRefType`. */
export type EvidenceRefType = "transaction" | "address" | "node" | "edge" | "document";

/** One pinned exhibit on one case. */
export interface CaseEvidence {
  id: string;
  investigationId: string;
  refType: EvidenceRefType;
  /** Canonical id of the referenced thing — a canonical tx id, a CAIP-10 account… */
  refId: string;
  chainId: string | null;
  /** The analyst's reason. The point of the table, and why it is not a string array. */
  note: string | null;
  /**
   * Caption captured AT PIN TIME.
   *
   * Stored rather than resolved on read so a case's exhibit list still reads
   * when the referenced row is unavailable to whoever is looking.
   */
  label: string | null;
  pinnedBy: string | null;
  createdAt: number | null;
}

/**
 * One case's exhibits, and whether that is all of them.
 *
 * The truncation flag is the point. The set feeds the feed's retention
 * exemption, where a missing exhibit is not a shorter list but a pinned row
 * quietly evicted from the case — so a caller has to be able to tell "no more"
 * from "stopped looking".
 */
export interface CaseEvidencePage {
  items: CaseEvidence[];
  truncated: boolean;
}

/** A case that has pinned some reference — the reverse lookup's row. */
export interface CaseReference {
  investigationId: string;
  title: string;
  status: InvestigationStatus;
  evidence: CaseEvidence;
}

export interface PinEvidenceInput {
  refType: EvidenceRefType;
  refId: string;
  chainId?: string | null;
  note?: string | null;
  label?: string | null;
}

export interface InvestigationsSource {
  /** Every investigation the caller can see, newest activity first. */
  list(): Promise<Investigation[]>;
  get(id: string): Promise<Investigation | null>;
  create(input: CreateInvestigationInput): Promise<Investigation>;
  update(investigation: Investigation): Promise<Investigation>;
  remove(id: string): Promise<void>;
  /**
   * The case's scope in the ONE shape every hand-off consumes.
   *
   * Server-derived rather than recomputed per button, because when each hand-off
   * derived its own they disagreed — one took every address, one took the FIRST,
   * one took none. `caseHandoffs.ts` keeps the local derivations for the surfaces
   * that need them synchronously; this is the authority the two are checked
   * against.
   */
  handoff(id: string): Promise<CaseHandoff>;

  /**
   * Pin an exhibit onto a case.
   *
   * Its own route because evidence is its own collection — it stopped being a
   * `pinned: string[]` on the case when a case outgrew one DynamoDB item. That
   * transition left a gap this closes: the menu went on pinning through
   * `update()`, whose PATCH body deliberately omits `pinned`, so the request
   * succeeded and pinned nothing while the UI reported success.
   *
   * Idempotent by reference server-side: pinning the same transaction twice
   * updates the note rather than producing a second exhibit, so a caller never
   * has to check first.
   */
  pinEvidence(id: string, input: PinEvidenceInput): Promise<CaseEvidence>;
  unpinEvidence(id: string, evidenceId: string): Promise<void>;
  /**
   * One case's exhibits, newest first.
   *
   * Walked rather than read once: the route pages at 50 and caps at 100, and a
   * long-running case outgrows that. The walk is bounded and reports the bound,
   * because a set summed over "however many pages happened to load" is a fact
   * about pagination rather than about the case.
   */
  listEvidence(id: string): Promise<CaseEvidencePage>;
  /**
   * Which cases have pinned this reference — filtered to what the caller may see.
   *
   * The question an analyst asks the moment a hash turns up somewhere else, and
   * the one the old `pinned: string[]` shape could only answer by reading every
   * case. Authorization is sharp here: the index is deliberately tenancy-free,
   * so a case the caller cannot open is not reported as existing — not even as a
   * count, which would still disclose that somebody is investigating it.
   */
  casesReferencing(refType: EvidenceRefType, refId: string): Promise<CaseReference[]>;
}

/**
 * One evidence row off the wire, or `null` when it is unusable.
 *
 * Dropped rather than half-built for the same reason a malformed watch is: an
 * exhibit with no reference cannot be rendered, navigated to, or unpinned, and a
 * blank row in a case's evidence list reads as data loss rather than as a row
 * that should not have been sent.
 */
function evidenceOf(raw: unknown): CaseEvidence | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const investigationId = str(raw.investigation_id);
  const refId = str(raw.ref_id);
  if (!id || !investigationId || !refId) return null;
  const createdAt = Number(raw.created_at);
  return {
    id,
    investigationId,
    refType: (str(raw.ref_type) ?? "transaction") as EvidenceRefType,
    refId,
    chainId: str(raw.chain_id),
    note: str(raw.note),
    label: str(raw.label),
    pinnedBy: str(raw.pinned_by),
    createdAt: Number.isFinite(createdAt) ? createdAt : null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(str).filter((v): v is string => v !== null) : [];
}

/**
 * Validate one persisted watch, dropping anything malformed.
 *
 * The server canonicalises and rejects on write, so this is the READ side of the
 * same seam — and the two want opposite behaviour. On the way in a malformed
 * watch is a client bug worth reporting; on the way out a row from an older
 * shape should degrade to "not shown" rather than break the page it appears on.
 *
 * The wire is snake_case, but the pre-cutover camelCase spellings are still
 * accepted: a case written by the saved-view build has `protocolId` /
 * `catalogId`, and silently dropping those watches would leave a scoped case
 * looking unscoped.
 */
function parseWatch(raw: unknown): Watch | null {
  if (!isRecord(raw)) return null;
  const kind = str(raw.kind);
  switch (kind) {
    case "protocol": {
      const protocolId = str(raw.protocol_id) ?? str(raw.protocolId);
      if (!protocolId) return null;
      const chains = strList(raw.chains);
      return chains.length ? { kind, protocolId, chains } : { kind, protocolId };
    }
    case "address": {
      const account = str(raw.account);
      if (!account) return null;
      const chains = strList(raw.chains);
      return chains.length ? { kind, account, chains } : { kind, account };
    }
    case "chain": {
      const chain = str(raw.chain);
      return chain ? { kind, chain } : null;
    }
    case "entity":
    case "tag":
    case "label":
    case "group": {
      const catalogId = str(raw.catalog_id) ?? str(raw.catalogId);
      if (!catalogId) return null;
      const name = str(raw.name);
      return name ? { kind, catalogId, name } : { kind, catalogId };
    }
    default:
      return null;
  }
}

/** A watch on the wire. Chains are already canonical CAIP-2 from the server. */
function watchToWire(watch: Watch): Record<string, unknown> {
  switch (watch.kind) {
    case "protocol":
      return { kind: "protocol", protocol_id: watch.protocolId, chains: watch.chains ?? [] };
    case "address":
      return { kind: "address", account: watch.account, chains: watch.chains ?? [] };
    case "chain":
      return { kind: "chain", chain: watch.chain };
    default:
      return { kind: watch.kind, catalog_id: watch.catalogId, name: watch.name };
  }
}

/**
 * Read a persisted feed window, accepting the legacy preset-string form.
 *
 * Cases saved before the window became a shape carry a bare
 * `window: "1h" | "24h" | "7d" | "all"`. Those are still meaningful, so they are
 * translated rather than reset — silently dropping a saved case's window back to
 * the default would change what an existing investigation shows the next time
 * someone opens it.
 */
function parseFeedWindow(raw: unknown): FeedWindow {
  if (typeof raw === "string") {
    if (raw === "all") return WINDOW_ALL;
    return FEED_WINDOW_PRESETS.includes(raw as TimeRangePreset)
      ? { kind: "rolling", preset: raw as TimeRangePreset }
      : DEFAULT_RETENTION.window;
  }
  if (!isRecord(raw)) return DEFAULT_RETENTION.window;
  if (raw.kind === "all") return WINDOW_ALL;
  if (raw.kind === "rolling") {
    return FEED_WINDOW_PRESETS.includes(raw.preset as TimeRangePreset)
      ? { kind: "rolling", preset: raw.preset as TimeRangePreset }
      : DEFAULT_RETENTION.window;
  }
  if (raw.kind === "absolute") {
    const since = Number(raw.since);
    const until = Number(raw.until);
    // Both bounds required and ordered: a half-written absolute window would
    // otherwise read as "since epoch", quietly turning a saved historical range
    // into an unbounded scan of the whole partition.
    if (Number.isFinite(since) && Number.isFinite(until) && since > 0 && since <= until) {
      return { kind: "absolute", since: Math.floor(since), until: Math.floor(until) };
    }
  }
  return DEFAULT_RETENTION.window;
}

function parseRetention(raw: unknown): Retention {
  if (!isRecord(raw)) return DEFAULT_RETENTION;
  const maxRows = Number(raw.max_rows ?? raw.maxRows);
  return {
    window: parseFeedWindow(raw.window),
    maxRows:
      Number.isFinite(maxRows) && maxRows > 0 ? Math.floor(maxRows) : DEFAULT_RETENTION.maxRows,
    // Only an explicit `false` turns it off, so a case saved before the field
    // existed gets the default rather than the falsy reading of `undefined`.
    balanceChains: (raw.balance_chains ?? raw.balanceChains) !== false,
  };
}

function retentionToWire(retention: Retention): Record<string, unknown> {
  const { window } = retention;
  return {
    window:
      window.kind === "rolling"
        ? { kind: "rolling", preset: window.preset }
        : window.kind === "absolute"
          ? { kind: "absolute", since: window.since, until: window.until }
          : { kind: "all" },
    max_rows: retention.maxRows,
    balance_chains: retention.balanceChains,
  };
}

function parseSaga(raw: unknown): InvestigationSaga {
  return raw === "bridge.ccip" || raw === "bridge.cctp" ? raw : null;
}

function parseStatus(raw: unknown): InvestigationStatus {
  return raw === "archived" ? "archived" : "open";
}

/**
 * The feed's tenancy tier, defaulted for cases saved before it was a field.
 *
 * Those cases were pinned to their VISIBILITY scope (`user` or `tenant`), so
 * inheriting that here would faithfully reproduce an empty feed: a transaction is
 * stored once per tier, and ingest lands in `global`. They get the default
 * instead.
 */
function parseFeedScope(raw: unknown): TxTenancyScopeId {
  return isTxTenancyScopeId(raw) ? raw : DEFAULT_INVESTIGATION_STATE.feedScope;
}

/**
 * The graph annotation layer, or `null` when the case has none.
 *
 * Validated harder than the rest: this body is folded onto every scene the case
 * renders, so a shape the renderer no longer understands would take the graph
 * down rather than just degrade it. A layer we cannot read is dropped — the
 * observed graph still draws.
 */
/**
 * The case's annotations, carried through unparsed.
 *
 * Deliberately NOT normalised here. The field holds one layer per subject, and a
 * case written before that holds a single flat body — telling them apart needs
 * the subject the reader is asking about, which this module does not know. Every
 * consumer goes through `annotationLayers` / `layerForSubject` instead, which
 * take both shapes and are the single place the two are reconciled.
 *
 * Parsing eagerly here is what the previous version did, and it would now
 * silently return `null` for every case in the new shape: a map of layers is not
 * a `SceneOverlay`, so the case would read as having no annotations at all.
 */
function parseAnnotations(raw: unknown): unknown {
  return isRecord(raw) ? raw : {};
}

/**
 * The case's table layout — a flat `{ columnId: visible }` map, or `null` when
 * nobody has chosen one. Non-boolean entries are dropped rather than coerced: a
 * junk value would silently hide a column, and no layout at all falls back to the
 * focus default, which is a better answer than a wrong one.
 */
function parseColumnVisibility(raw: unknown): Record<string, boolean> | null {
  if (!isRecord(raw)) return null;
  const columns: Record<string, boolean> = {};
  for (const [id, visible] of Object.entries(raw)) {
    if (typeof visible === "boolean") columns[id] = visible;
  }
  return Object.keys(columns).length > 0 ? columns : null;
}

/**
 * The backend ladder narrowed to the two tiers the UI offers.
 *
 * A case at `org` or `customer` scope reads as Shared, which is honest — it IS
 * shared, just more widely than this picker can express — while `user` stays
 * Personal. Guessing the other way would show an org-wide case as private.
 */
function parseScope(raw: unknown): InvestigationScope {
  return raw === "user" ? "user" : "tenant";
}

function investigationFromWire(row: unknown): Investigation | null {
  if (!isRecord(row)) return null;
  const id = str(row.id);
  if (!id) return null;

  const watches = Array.isArray(row.watches)
    ? row.watches.map(parseWatch).filter((w): w is Watch => w !== null)
    : [];
  const lastActivityAt = Number(row.last_activity_at) * 1000;
  const createdAt = Number(row.created_at) * 1000;
  const updatedAt = Number(row.updated_at ?? row.created_at) * 1000;

  return {
    id,
    title: str(row.title) ?? "Untitled investigation",
    scope: parseScope(row.scope),
    createdBy: str(row.created_by),
    watches,
    retention: parseRetention(row.retention),
    feedScope: parseFeedScope(row.feed_scope),
    status: parseStatus(row.status),
    saga: parseSaga(row.saga),
    annotations: parseAnnotations(row.annotations),
    columnVisibility: parseColumnVisibility(row.column_visibility),
    // A blank id reads as "the system default" rather than being carried through
    // as `""`, which would submit an empty pipeline_id that means the same thing
    // by accident instead of by decision.
    pipelineId: str(row.pipeline_id),
    // Validated per act rather than trusted: one malformed entry should be absent
    // from the narrative panel, not break it.
    acts: parseActs(row.acts),
    // Epoch SECONDS on the wire (matching `block_timestamp` and every other
    // timestamp the platform stores), MILLISECONDS in the FE. Getting this
    // backwards is a live hazard — a seconds value read as ms puts every case in
    // 1970, and an ms value read as seconds puts them in the year 55000.
    lastActivityAt: Number.isFinite(lastActivityAt) ? lastActivityAt : updatedAt,
    // The server's own counter. `pinned` used to be read here and has not been
    // sent since evidence became its own collection.
    evidenceCount: Number.isFinite(Number(row.evidence_count))
      ? Math.max(0, Number(row.evidence_count))
      : 0,
    createdAt: Number.isFinite(createdAt) ? createdAt : Date.now(),
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : Date.now(),
  };
}

/**
 * The PATCH body for a case.
 *
 * Deliberately partial: a case is co-edited, and sending the whole object would
 * revert whatever a colleague changed between this client's read and its write.
 * `pinned` is absent because evidence is its own collection with its own routes —
 * it is not a field on the case.
 */
function investigationToPatch(investigation: Investigation): Record<string, unknown> {
  return {
    title: investigation.title,
    scope: investigation.scope,
    status: investigation.status,
    watches: investigation.watches.map(watchToWire),
    retention: retentionToWire(investigation.retention),
    feed_scope: investigation.feedScope,
    saga: investigation.saga,
    pipeline_id: investigation.pipelineId,
    acts: investigation.acts,
    annotations: investigation.annotations ?? {},
    column_visibility: investigation.columnVisibility ?? {},
  };
}

function handoffFromWire(raw: unknown): CaseHandoff {
  const row = isRecord(raw) ? raw : {};
  const unrepresented = Array.isArray(row.unrepresented) ? row.unrepresented : [];
  return {
    investigationId: str(row.investigation_id) ?? "",
    addresses: strList(row.addresses),
    protocolIds: strList(row.protocol_ids),
    chainIds: strList(row.chain_ids),
    unrepresented: unrepresented.filter(isRecord).map((w) => ({
      kind: str(w.kind) ?? "unknown",
      catalogId: str(w.catalog_id) ?? "",
      name: str(w.name),
    })),
    feedScope: parseFeedScope(row.feed_scope),
    pipelineId: str(row.pipeline_id),
  };
}

function byActivityDesc(a: Investigation, b: Investigation): number {
  return Math.max(b.lastActivityAt, b.updatedAt) - Math.max(a.lastActivityAt, a.updatedAt);
}

/** Unwrap `{ items, next_cursor }` / `{ investigation }` envelopes. */
function itemsOf(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (isRecord(payload) && Array.isArray(payload.items)) return payload.items;
  return [];
}

function investigationOf(payload: unknown): Investigation | null {
  if (!isRecord(payload)) return null;
  return investigationFromWire(payload.investigation ?? payload);
}

/**
 * The investigations index over `investigation-mgmt-api`.
 *
 * `list()` pages until the server stops handing back a cursor. That is safe
 * because the page is already filtered to what the caller can see and a tenant
 * holds tens of cases, not thousands — and it is NECESSARY because the server
 * filters private and archived cases out of each page rather than out of the
 * query (pushing them into a DynamoDB `FilterExpression` would make a page's size
 * unpredictable), so a first page can legitimately come back short with a cursor.
 */
export function createApiInvestigationsSource(client: ApiClient): InvestigationsSource {
  const listAll = async (): Promise<Investigation[]> => {
    const rows: Investigation[] = [];
    let cursor: string | undefined;
    // Bounded so a server that kept returning a cursor cannot spin the browser.
    for (let page = 0; page < 20; page += 1) {
      const query: Record<string, string> = { limit: "100" };
      if (cursor) query.cursor = cursor;
      const payload = unwrapData(await client.get<Record<string, unknown>>(BASE, { query }));
      for (const raw of itemsOf(payload)) {
        const row = investigationFromWire(raw);
        if (row) rows.push(row);
      }
      cursor = isRecord(payload) ? (str(payload.next_cursor) ?? undefined) : undefined;
      if (!cursor) break;
    }
    return rows.sort(byActivityDesc);
  };

  return {
    list: listAll,

    async get(id) {
      const payload = unwrapData(
        await client.get<Record<string, unknown>>(`${BASE}/${encodeURIComponent(id)}`),
      );
      return investigationOf(payload);
    },

    async create(input) {
      const payload = unwrapData(
        await client.post<Record<string, unknown>>(BASE, {
          title: input.title.trim() || "Untitled investigation",
          scope: input.scope,
          watches: (input.watches ?? []).map(watchToWire),
          retention: retentionToWire({ ...DEFAULT_RETENTION, ...input.retention }),
          saga: input.saga ?? null,
          feed_scope: DEFAULT_INVESTIGATION_STATE.feedScope,
        }),
      );
      const created = investigationOf(payload);
      if (!created) throw new Error("investigation-mgmt-api returned no investigation");
      // Drop the short GET cache so the list reflects the new case immediately.
      client.invalidate(BASE);
      return created;
    },

    async update(investigation) {
      const path = `${BASE}/${encodeURIComponent(investigation.id)}`;
      const payload = unwrapData(
        await client.patch<Record<string, unknown>>(path, investigationToPatch(investigation)),
      );
      const updated = investigationOf(payload);
      if (!updated) throw new Error("investigation-mgmt-api returned no investigation");
      client.invalidate(BASE);
      client.invalidate(path);
      return updated;
    },

    async remove(id) {
      const path = `${BASE}/${encodeURIComponent(id)}`;
      await client.delete<void>(path);
      client.invalidate(BASE);
      client.invalidate(path);
    },

    async pinEvidence(id, input) {
      const path = `${BASE}/${encodeURIComponent(id)}/evidence`;
      const payload = unwrapData(
        await client.post<Record<string, unknown>>(path, {
          ref_type: input.refType,
          ref_id: input.refId,
          ...(input.chainId ? { chain_id: input.chainId } : {}),
          // `undefined`, not falsy. Re-pinning is an UPDATE server-side, and it
          // keeps whatever field the body omits — so omitting on falsy would
          // make an empty note mean "leave the old one", and clearing a note
          // impossible. Omitted preserves; `""` clears; text replaces.
          ...(input.note !== undefined ? { note: input.note } : {}),
          ...(input.label !== undefined ? { label: input.label } : {}),
        }),
      );
      const row = evidenceOf(isRecord(payload) ? payload.evidence : null);
      if (!row) throw new Error("investigation-mgmt-api returned no evidence row");
      // The case's `updatedAt` and `evidence_count` move with a pin (the server
      // touches both), and the reverse index now has a new entry — so the case
      // reads and every "pinned in N cases" badge for this reference are stale.
      //
      // `invalidateMatching`, not `invalidate`: the exact-URL form only reaches
      // a read the writer can name in full, query string included. The exhibit
      // list is read per page (`?limit=&cursor=`) and `REFERENCES` is a prefix
      // that is never itself a GET URL, so naming them exactly invalidated
      // nothing and the next read was served the pre-write body.
      client.invalidate(BASE);
      client.invalidateMatching(path);
      client.invalidateMatching(REFERENCES);
      return row;
    },

    async unpinEvidence(id, evidenceId) {
      const path = `${BASE}/${encodeURIComponent(id)}/evidence`;
      await client.delete<void>(`${path}/${encodeURIComponent(evidenceId)}`);
      client.invalidate(BASE);
      client.invalidateMatching(path);
      client.invalidateMatching(REFERENCES);
    },

    async listEvidence(id) {
      const path = `${BASE}/${encodeURIComponent(id)}/evidence`;
      const items: CaseEvidence[] = [];
      let cursor: string | null = null;
      let truncated = false;
      for (let page = 0; page < MAX_EVIDENCE_PAGES; page += 1) {
        const payload: unknown = unwrapData(
          await client.get<Record<string, unknown>>(path, {
            query: { limit: EVIDENCE_PAGE_SIZE, cursor: cursor ?? undefined },
          }),
        );
        const raw = isRecord(payload) && Array.isArray(payload.items) ? payload.items : [];
        for (const row of raw) {
          const parsed = evidenceOf(row);
          if (parsed) items.push(parsed);
        }
        cursor = isRecord(payload) ? str(payload.next_cursor) : null;
        if (!cursor) break;
        truncated = page === MAX_EVIDENCE_PAGES - 1;
      }
      return { items, truncated };
    },

    async casesReferencing(refType, refId) {
      if (!refId) return [];
      const path = `${REFERENCES}/${encodeURIComponent(refType)}/${encodeURIComponent(refId)}`;
      const payload = unwrapData(await client.get<Record<string, unknown>>(path));
      const rows = isRecord(payload) && Array.isArray(payload.results) ? payload.results : [];
      const out: CaseReference[] = [];
      for (const raw of rows) {
        if (!isRecord(raw)) continue;
        const evidence = evidenceOf(raw.evidence);
        const investigationId = str(raw.investigation_id);
        if (!evidence || !investigationId) continue;
        out.push({
          investigationId,
          title: str(raw.title) ?? "Untitled investigation",
          status: parseStatus(raw.status),
          evidence,
        });
      }
      return out;
    },

    async handoff(id) {
      return handoffFromWire(
        unwrapData(
          await client.get<Record<string, unknown>>(
            `${BASE}/${encodeURIComponent(id)}/handoff`,
          ),
        ),
      );
    },
  };
}

export type { CaseAct };
